import React, { useState, useEffect, useMemo } from 'react';
import { employeeAPI } from '../../../kindergartens/detail/components/Employee/actions';
import { usersAPI } from '../../../adminDOU/components/Users/actions';
import { config } from '../../../adminDOU/data/config';

// ─── Константи ────────────────────────────────────────────────────────────────

// Системні ролі доступні для співробітників садка
const KG_SYSTEM_ROLES = [
  { value: 'kindergartenHead', label: 'Директор' },
  { value: 'educator',         label: 'Вихователь' },
  { value: 'methodist',        label: 'Вихователь-методист' },
  { value: 'nurse',            label: 'Медична сестра' },
];

const DEFAULT_GRANT = 'R'; // мінімальні права за замовчуванням

// ─── Утиліти ─────────────────────────────────────────────────────────────────

const validateEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v?.trim() || '');

const getInitials = (emp) =>
  [emp.lastName, emp.firstName].filter(Boolean).map(n => n[0]).join('').toUpperCase() || '?';

const getPositionType = (emp) =>
  emp.departments?.[0]?.positionType || '';

// ─── Підкомпоненти ────────────────────────────────────────────────────────────

const AccessBadge = ({ user }) => {
  if (!user) return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '5px',
      fontSize: '12px', color: '#9ca3af'
    }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#d1d5db', display: 'inline-block' }} />
      Немає доступу
    </span>
  );
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '12px',
      color: user.isActive ? '#065f46' : '#92400e',
      fontWeight: 500
    }}>
      <span style={{
        width: 7, height: 7, borderRadius: '50%',
        background: user.isActive ? '#10b981' : '#f59e0b',
        display: 'inline-block'
      }} />
      {user.isActive ? 'Активний' : 'Деактивований'}
    </span>
  );
};

// ─── Модалка надання/редагування доступу ─────────────────────────────────────

const AccessModal = ({ employee, existingUser, kindergartenId, onSave, onRevoke, onClose }) => {
  const isEdit = !!existingUser;

  const [email, setEmail] = useState(existingUser?.email || '');
  const [role, setRole] = useState(existingUser?.role || KG_SYSTEM_ROLES[0].value);
  const [grant, setGrant] = useState(existingUser?.grant || DEFAULT_GRANT);
  const [authType, setAuthType] = useState(existingUser?.privelegesAuthType || Object.keys(config.authType)[0]);
  const [emailTouched, setEmailTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const emailError = emailTouched && !validateEmail(email) ? 'Введіть коректний email' : '';

  const handleSave = async () => {
    setEmailTouched(true);
    if (!validateEmail(email)) return;

    setSaving(true);
    setError('');
    try {
      const userData = {
        firstName:  employee.firstName,
        lastName:   employee.lastName,
        middleName: employee.middleName,
        email:      email.trim(),
        role,
        area:       kindergartenId,
        grant,
        privelegesAuthType: authType,
        isActive:   true,
        // Паспортні / інші поля залишаємо порожніми — заповнить адмін пізніше
      };
      await onSave(userData, existingUser?._id);
    } catch (e) {
      setError(e.message || 'Помилка збереження');
    } finally {
      setSaving(false);
    }
  };

  const initials = getInitials(employee);

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1050
    }} onClick={onClose}>
      <div style={{
        background: '#fff', borderRadius: '12px', width: '100%', maxWidth: '480px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)', overflow: 'hidden'
      }} onClick={e => e.stopPropagation()}>

        {/* Хедер */}
        <div style={{
          padding: '20px 24px 16px',
          borderBottom: '1px solid #f0f0f0',
          background: 'linear-gradient(135deg, #f0f4ff 0%, #fafafa 100%)',
          display: 'flex', alignItems: 'center', gap: '14px'
        }}>
          <div style={{
            width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
            background: 'linear-gradient(135deg, #3b82f6, #6366f1)',
            color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '16px', fontWeight: 700
          }}>
            {initials}
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: '15px', color: '#111827' }}>
              {employee.fullName}
            </div>
            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
              {employee.position}
              {getPositionType(employee) && (
                <span style={{ marginLeft: '6px', color: '#9ca3af' }}>· {getPositionType(employee)}</span>
              )}
            </div>
          </div>
          <button onClick={onClose} style={{
            marginLeft: 'auto', background: 'none', border: 'none',
            fontSize: '20px', color: '#9ca3af', cursor: 'pointer', lineHeight: 1
          }}>×</button>
        </div>

        {/* Форма */}
        <div style={{ padding: '20px 24px' }}>
          <div style={{
            fontSize: '11px', fontWeight: 700, color: '#6b7280',
            textTransform: 'uppercase', letterSpacing: '0.07em',
            marginBottom: '16px'
          }}>
            {isEdit ? 'Параметри доступу' : 'Надати доступ до системи'}
          </div>

          {/* Email */}
          <div style={{ marginBottom: '14px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '5px' }}>
              Email для входу *
            </label>
            <input
              value={email}
              onChange={e => setEmail(e.target.value)}
              onBlur={() => setEmailTouched(true)}
              placeholder="example@gmail.com"
              style={{
                width: '100%', padding: '9px 12px', fontSize: '14px', boxSizing: 'border-box',
                border: `1px solid ${emailError ? '#dc3545' : '#d1d5db'}`,
                borderRadius: '6px', outline: 'none',
                background: emailError ? '#fff5f5' : '#fff'
              }}
            />
            {emailError && (
              <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px' }}>{emailError}</div>
            )}
          </div>

          {/* Системна роль */}
          <div style={{ marginBottom: '14px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '5px' }}>
              Системна роль *
            </label>
            <select
              value={role}
              onChange={e => setRole(e.target.value)}
              style={{
                width: '100%', padding: '9px 12px', fontSize: '14px',
                border: '1px solid #d1d5db', borderRadius: '6px',
                outline: 'none', background: '#fff', cursor: 'pointer'
              }}
            >
              {KG_SYSTEM_ROLES.map(r => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>

          {/* Тип авторизації */}
          <div style={{ marginBottom: '14px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '5px' }}>
              Тип авторизації
            </label>
            <select
              value={authType}
              onChange={e => setAuthType(e.target.value)}
              style={{
                width: '100%', padding: '9px 12px', fontSize: '14px',
                border: '1px solid #d1d5db', borderRadius: '6px',
                outline: 'none', background: '#fff', cursor: 'pointer'
              }}
            >
              {Object.entries(config.authType).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>

          {/* Права */}
          <div style={{ marginBottom: '6px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '5px' }}>
              Права
            </label>
            <input
              value={grant}
              onChange={e => setGrant(e.target.value)}
              placeholder="CRUD"
              style={{
                width: '100%', padding: '9px 12px', fontSize: '14px', boxSizing: 'border-box',
                border: '1px solid #d1d5db', borderRadius: '6px',
                outline: 'none', background: '#fff', fontFamily: 'monospace'
              }}
            />
          </div>

          {error && (
            <div style={{
              marginTop: '12px', padding: '10px 14px', background: '#fee2e2',
              color: '#dc3545', borderRadius: '6px', fontSize: '13px'
            }}>
              {error}
            </div>
          )}
        </div>

        {/* Футер */}
        <div style={{
          padding: '14px 24px', borderTop: '1px solid #f0f0f0',
          background: '#fafafa', display: 'flex', justifyContent: 'space-between', alignItems: 'center'
        }}>
          <div>
            {isEdit && onRevoke && (
              <button
                onClick={() => onRevoke(existingUser)}
                disabled={saving}
                style={{
                  padding: '8px 14px', borderRadius: '6px', fontSize: '13px', fontWeight: 600,
                  cursor: 'pointer', border: '1px solid #fca5a5',
                  background: '#fff5f5', color: '#dc3545'
                }}
              >
                {existingUser.isActive ? 'Деактивувати' : 'Активувати'}
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={onClose} style={{
              padding: '8px 18px', borderRadius: '6px', fontSize: '13px',
              cursor: 'pointer', border: '1px solid #e5e7eb', background: '#fff', color: '#6b7280'
            }}>
              Скасувати
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                padding: '8px 18px', borderRadius: '6px', fontSize: '13px', fontWeight: 600,
                cursor: saving ? 'not-allowed' : 'pointer',
                border: 'none', background: saving ? '#93c5fd' : '#3b82f6', color: '#fff'
              }}
            >
              {saving ? 'Збереження…' : isEdit ? 'Зберегти' : 'Надати доступ'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Головний компонент ───────────────────────────────────────────────────────

const KindergartenAccess = ({ kindergartenId }) => {
  const [employees, setEmployees] = useState([]);
  const [kgUsers, setKgUsers] = useState([]);   // Users записи прив'язані до цього садка
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modal, setModal] = useState(null);     // { employee, existingUser|null }
  const [search, setSearch] = useState('');
  const [filterAccess, setFilterAccess] = useState('all'); // all | granted | none

  useEffect(() => {
    loadData();
  }, [kindergartenId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [empRes, usersRes] = await Promise.all([
        employeeAPI.getActiveEmployees(kindergartenId),
        usersAPI.getUsers()
      ]);

      setEmployees(empRes);

      // Фільтруємо Users тільки цього садка
      const kgUsersList = (usersRes.data || []);
      setKgUsers(kgUsersList);
    } catch (e) {
      setError(e.message || 'Помилка завантаження');
    } finally {
      setLoading(false);
    }
  };

  // Мерджимо співробітників з їх Users-записами по email або rnokpp
  const mergedList = useMemo(() => {
    return employees.map(emp => {
      // Шукаємо Users-запис: намагаємось по rnokpp (якщо є поле code), або залишаємо null
      const user = kgUsers.find(u => u.code === emp.rnokpp) || null;
      return { emp, user };
    });
  }, [employees, kgUsers]);

  // Фільтрація
  const filteredList = useMemo(() => {
    return mergedList.filter(({ emp, user }) => {
      if (filterAccess === 'granted' && !user) return false;
      if (filterAccess === 'none' && user) return false;

      if (search.trim()) {
        const q = search.toLowerCase();
        return emp.fullName.toLowerCase().includes(q) ||
          emp.position.toLowerCase().includes(q) ||
          (user?.email || '').toLowerCase().includes(q);
      }
      return true;
    });
  }, [mergedList, filterAccess, search]);

  const stats = useMemo(() => ({
    total: mergedList.length,
    granted: mergedList.filter(({ user }) => user?.isActive).length,
    inactive: mergedList.filter(({ user }) => user && !user.isActive).length,
  }), [mergedList]);

  const handleSave = async (userData, existingId) => {
    if (existingId) {
      await usersAPI.updateUser(existingId, userData);
    } else {
      await usersAPI.addUser(userData);
    }
    setModal(null);
    await loadData();
  };

  const handleRevoke = async (user) => {
    await usersAPI.updateUser(user._id, { ...user, isActive: !user.isActive });
    setModal(null);
    await loadData();
  };

  if (loading) return (
    <div style={{ textAlign: 'center', padding: '60px', color: '#9ca3af', fontSize: '15px' }}>
      Завантаження…
    </div>
  );

  if (error) return (
    <div style={{
      margin: '20px', padding: '14px 18px', background: '#fee2e2',
      color: '#dc3545', borderRadius: '8px', fontSize: '14px'
    }}>
      {error}
    </div>
  );

  return (
    <div style={{ fontFamily: 'system-ui, sans-serif' }}>

      {/* ── Статистика ── */}
      <div style={{ display: 'flex', gap: '12px', padding: '16px 20px', flexWrap: 'wrap' }}>
        {[
          { label: 'Всього співробітників', value: stats.total, color: '#6b7280' },
          { label: 'Доступ активний',       value: stats.granted, color: '#10b981' },
          { label: 'Деактивовано',          value: stats.inactive, color: '#f59e0b' },
          { label: 'Без доступу',           value: stats.total - stats.granted - stats.inactive, color: '#d1d5db' },
        ].map(s => (
          <div key={s.label} style={{
            flex: '1 1 120px', padding: '12px 16px', background: '#fff',
            borderRadius: '8px', border: '1px solid #e5e7eb',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}>
            <div style={{ fontSize: '22px', fontWeight: 700, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* ── Фільтри ── */}
      <div style={{
        display: 'flex', gap: '10px', padding: '0 20px 14px',
        alignItems: 'center', flexWrap: 'wrap'
      }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Пошук за ім'ям, посадою, email…"
          style={{
            flex: '1 1 260px', padding: '8px 12px', fontSize: '14px',
            border: '1px solid #d1d5db', borderRadius: '6px', outline: 'none'
          }}
        />
        {[
          { value: 'all',     label: 'Всі' },
          { value: 'granted', label: 'З доступом' },
          { value: 'none',    label: 'Без доступу' },
        ].map(f => (
          <button
            key={f.value}
            onClick={() => setFilterAccess(f.value)}
            style={{
              padding: '8px 14px', borderRadius: '6px', fontSize: '13px',
              fontWeight: filterAccess === f.value ? 600 : 400,
              border: '1px solid',
              borderColor: filterAccess === f.value ? '#3b82f6' : '#e5e7eb',
              background: filterAccess === f.value ? '#eff6ff' : '#fff',
              color: filterAccess === f.value ? '#3b82f6' : '#6b7280',
              cursor: 'pointer'
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* ── Таблиця ── */}
      <div style={{ margin: '0 20px 20px', borderRadius: '8px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
          <thead>
            <tr style={{ background: '#f9fafb' }}>
              {['№', 'Співробітник', 'Посада', 'Доступ', 'Email', 'Роль у системі', ''].map((h, i) => (
                <th key={i} style={{
                  padding: '10px 14px', textAlign: 'left', fontWeight: 600,
                  fontSize: '11px', color: '#6b7280', textTransform: 'uppercase',
                  letterSpacing: '0.05em', borderBottom: '1px solid #e5e7eb',
                  whiteSpace: 'nowrap'
                }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredList.length === 0 && (
              <tr>
                <td colSpan={7} style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>
                  Нічого не знайдено
                </td>
              </tr>
            )}
            {filteredList.map(({ emp, user }, idx) => (
              <tr key={emp.id} style={{
                background: idx % 2 === 0 ? '#fff' : '#f9fafb',
                borderBottom: '1px solid #f0f0f0'
              }}>
                <td style={{ padding: '12px 14px', color: '#9ca3af', fontSize: '13px' }}>
                  {idx + 1}
                </td>
                <td style={{ padding: '12px 14px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{
                      width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                      background: user
                        ? 'linear-gradient(135deg, #3b82f6, #6366f1)'
                        : 'linear-gradient(135deg, #d1d5db, #9ca3af)',
                      color: '#fff', display: 'flex', alignItems: 'center',
                      justifyContent: 'center', fontSize: '12px', fontWeight: 700
                    }}>
                      {getInitials(emp)}
                    </div>
                    <div>
                      <div style={{ fontWeight: 500, color: '#111827' }}>{emp.fullName}</div>
                    </div>
                  </div>
                </td>
                <td style={{ padding: '12px 14px', color: '#6b7280', fontSize: '13px' }}>
                  {emp.position}
                  <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '1px' }}>
                    {getPositionType(emp)}
                  </div>
                </td>
                <td style={{ padding: '12px 14px' }}>
                  <AccessBadge user={user} />
                </td>
                <td style={{ padding: '12px 14px', color: '#6b7280', fontSize: '13px', fontFamily: 'monospace' }}>
                  {user?.email || '—'}
                </td>
                <td style={{ padding: '12px 14px', color: '#6b7280', fontSize: '13px' }}>
                  {user
                    ? (KG_SYSTEM_ROLES.find(r => r.value === user.role)?.label || user.role)
                    : '—'
                  }
                </td>
                <td style={{ padding: '12px 14px', textAlign: 'right' }}>
                  <button
                    onClick={() => setModal({ employee: emp, existingUser: user })}
                    style={{
                      padding: '6px 14px', borderRadius: '6px', fontSize: '12px', fontWeight: 600,
                      cursor: 'pointer', border: '1px solid',
                      borderColor: user ? '#d1d5db' : '#3b82f6',
                      background: user ? '#fff' : '#3b82f6',
                      color: user ? '#374151' : '#fff',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {user ? 'Налаштувати' : 'Надати доступ'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Модалка ── */}
      {modal && (
        <AccessModal
          employee={modal.employee}
          existingUser={modal.existingUser}
          kindergartenId={kindergartenId}
          onSave={handleSave}
          onRevoke={modal.existingUser ? handleRevoke : null}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
};

export default KindergartenAccess;